import * as webpDecode from '@jsquash/webp/decode';
import { parseGIF, decompressFrames } from 'gifuct-js';
import {getMimeTypeFromExtension} from "@/components/graphics/Graphics";

const WEBP_DEC_WASM_LOCATION = "/wasm/webp_dec.wasm";
let wasmModule = null;


export class GIFDecoderAdapter {
	constructor({ data, type }) {
		if (type !== 'image/gif') {
			throw new Error(`GIFDecoderAdapter only supports image/gif, got ${type}`);
		}

		this.type = type;
		this.data = data;
		this.frames = [];
		this.closed = false;

		this.tracks = {
			ready: null,
			selectedTrack: {
				frameCount: 0,
				animated: false,
				repetitionCount: 0
			}
		};

		this._readyPromise = this._init();
		this.tracks.ready = this._readyPromise;
	}

	async _init() {
		try {
			const arrayBuffer = await normalizeToArrayBuffer(this.data);
			const gif = parseGIF(arrayBuffer);
			const width = gif?.lsd?.width ?? 0;
			const height = gif?.lsd?.height ?? 0;
			const decodedFrames = decompressFrames(gif, true);
			const { ctx } = createCompositingSurface(width, height);

			this.frames = decodedFrames.map((frame) => {
				const { left, top, width: patchWidth, height: patchHeight } = frame.dims;
				const patchImage = new ImageData(frame.patch, patchWidth, patchHeight);
				const restorePoint = frame.disposalType === 3 ? ctx.getImageData(0, 0, width, height) : null;

				// Draw patch through an intermediate surface so alpha=0 pixels do not erase prior canvas pixels.
				drawPatchWithAlpha(ctx, patchImage, left, top);
				const composited = ctx.getImageData(0, 0, width, height);

				if (frame.disposalType === 2) {
					ctx.clearRect(left, top, patchWidth, patchHeight);
				} else if (frame.disposalType === 3 && restorePoint) {
					ctx.putImageData(restorePoint, 0, 0);
				}

				return {
					imageData: composited,
					duration: frame.delay ?? 100
				};
			});

			this.tracks.selectedTrack.frameCount = this.frames.length;
			this.tracks.selectedTrack.animated = this.frames.length > 1;
			this.tracks.selectedTrack.repetitionCount = 0;
		} catch (e) {
			console.error("GIF decode error:", e);
			throw new Error("GIF decode failed: " + e.message);
		}
	}

	async decode({ frameIndex = 0 } = {}) {
		await this._readyPromise;

		if (this.closed) {
			throw new Error('Decoder is closed');
		}

		const frame = this.frames[frameIndex];
		if (!frame) {
			throw new Error(`Frame ${frameIndex} out of range`);
		}

		const { imageData, duration } = frame;
		imageData.duration = duration * 1000; // Convert to microseconds

		return {
			image: imageData,
			complete: true,
			duration
		};
	}

	close() {
		if (this.closed) return;
		this.closed = true;
		this.frames = [];
	}
}



export class LibWebPDecoderAdapter {
	constructor({ data, type }) {
		if (type !== 'image/webp') {
			throw new Error(`LibWebPDecoderAdapter only supports image/webp, got ${type}`);
		}

		this.type = type;
		this.data = data;
		this.frames = [];
		this.width = 0;
		this.height = 0;
		this.closed = false;

		this.tracks = {
			ready: null,
			selectedTrack: {
				frameCount: 0,
				animated: false,
				repetitionCount: 0
			}
		};

		this._readyPromise = this._init();
		this.tracks.ready = this._readyPromise;
	}

	async _init() {
		try {
			const arrayBuffer = await normalizeToArrayBuffer(this.data);
			await ensureWebPWasm();

			// Parse the RIFF/WEBP container to extract per-frame ANMF metadata.
			// This lets us composite frames ourselves (blend + dispose) instead of
			// relying on WebPAnimDecoder, whose disposal handling produces trail
			// artifacts on animations with transparent backgrounds.
			const animation = parseWebPAnimation(arrayBuffer);

			if (!animation) {
				// Not an ANMF animation or unrecognised container — fall back to
				// the bulk decoder (single-frame or simple animations).
				const rawFrames = await webpDecode.decodeAnimated(arrayBuffer);
				if (!rawFrames || rawFrames.length === 0) throw new Error('No frames decoded');
				const { width, height } = rawFrames[0].imageData;
				const { ctx } = createCompositingSurface(width, height);
				this.frames = rawFrames.map(({ imageData, duration }) => {
					ctx.clearRect(0, 0, width, height);
					drawPatchWithAlpha(ctx, imageData, 0, 0);
					return { imageData: ctx.getImageData(0, 0, width, height), duration };
				});
			} else {
				const { canvasWidth: W, canvasHeight: H, frames: anmfFrames } = animation;
				const { ctx } = createCompositingSurface(W, H);
				ctx.clearRect(0, 0, W, H); // canvas starts fully transparent

				this.frames = [];
				for (const { x, y, fw, fh, duration, blendMethod, disposeMethod, frameData } of anmfFrames) {
					// Decode this ANMF frame as a standalone WebP image.
					let patchData;
					try {
						patchData = await webpDecode.default(wrapFrameInWebP(frameData, fw, fh));
					} catch (frameErr) {
						const hdr = new Uint8Array(frameData, 0, Math.min(8, frameData.byteLength));
						const tag = String.fromCharCode(hdr[0], hdr[1], hdr[2], hdr[3]);
						throw new Error("ANMF frame decode failed (frame #" + this.frames.length + ", tag=" + tag + ", frameSize=" + frameData.byteLength + "): " + frameErr.message);
					}

					if (blendMethod === 1) {
						// Replace mode: copy pixels directly, bypassing alpha compositing.
						// putImageData writes the exact RGBA values (including alpha=0 → transparent).
						ctx.putImageData(patchData, x, y);
					} else {
						// Blend mode: alpha-composite the patch over the existing canvas.
						drawPatchWithAlpha(ctx, patchData, x, y);
					}

					// Snapshot the full composited canvas as this frame's image.
					this.frames.push({
						imageData: ctx.getImageData(0, 0, W, H),
						duration
					});

					// Apply post-frame disposal.
					if (disposeMethod === 1) {
						// Dispose to background: clear the frame's rectangle to transparent.
						ctx.clearRect(x, y, fw, fh);
					}
					// disposeMethod === 0: leave canvas unchanged for next frame.
				}
			}
		} catch (e) {
			throw new Error("WebP decode failed: " + e);
		}

		this.tracks.selectedTrack.frameCount = this.frames.length;
		this.tracks.selectedTrack.animated   = this.frames.length > 1;
		this.tracks.selectedTrack.repetitionCount = 0;
	}

	async decode({ frameIndex = 0 } = {}) {
		await this._readyPromise;

		if (this.closed) {
			throw new Error('Decoder is closed');
		}

		const frame = this.frames[frameIndex];
		if (!frame) {
			throw new Error(`Frame ${frameIndex} out of range`);
		}

		const {imageData} = frame;

		imageData.duration = frame.duration * 1000;

		return {
			image: imageData,
			complete: true,
			duration: frame.duration
		};
	}

	close() {
		if (this.closed) return;
		this.closed = true;

		for (const frame of this.frames) {
			try {
				frame?.bitmap?.close?.();
			} catch (_error) {
				// ignore
			}
		}

		this.frames = [];
	}
}

// ---------------------------------------------------------------------------
// WebP WASM helpers
// ---------------------------------------------------------------------------

/**
 * Ensures the WebP WASM decoder module is compiled and initialised.
 * Idempotent — safe to call multiple times concurrently.
 */
async function ensureWebPWasm() {
	if (!wasmModule) {
		try {
			const wasmFile   = await fetch(WEBP_DEC_WASM_LOCATION);
			const wasmBuffer = await wasmFile.arrayBuffer();
			wasmModule = await WebAssembly.compile(wasmBuffer);
			await webpDecode.init(wasmModule);
		} catch (e) {
			console.error("WebP WASM Init Failed:", e);
			wasmModule = null;
			throw new Error("WebP WASM Init failed: " + e.message);
		}
	}
}

/**
 * Parse the RIFF/WEBP container and return per-frame ANMF metadata.
 *
 * Each entry contains:
 *   x, y          – frame origin in canvas pixels
 *   fw, fh        – frame dimensions in pixels
 *   duration      – display duration in milliseconds
 *   blendMethod   – 0 = alpha-blend over canvas, 1 = replace (no blend)
 *   disposeMethod – 0 = leave canvas, 1 = clear frame rect to transparent
 *   frameData     – ArrayBuffer of raw VP8/VP8L/VP8X+ALPH chunk bytes
 *
 * Returns null when the file is not an animated ANMF WebP.
 */
function parseWebPAnimation(buffer) {
	const u8  = new Uint8Array(buffer);
	const dv  = new DataView(buffer);
	const tag = (o) => String.fromCharCode(u8[o], u8[o+1], u8[o+2], u8[o+3]);
	const u24 = (o) => u8[o] | (u8[o+1] << 8) | (u8[o+2] << 16);

	if (tag(0) !== 'RIFF' || tag(8) !== 'WEBP') return null;

	let canvasWidth = 0, canvasHeight = 0;
	const frames = [];
	let offset = 12;

	while (offset + 8 <= buffer.byteLength) {
		const id        = tag(offset);
		const size      = dv.getUint32(offset + 4, true);
		const dataStart = offset + 8;

		if (id === 'VP8X' && size >= 10) {
			// Canvas dimensions are stored as (value − 1) in 24-bit LE fields.
			canvasWidth  = u24(dataStart + 4) + 1;
			canvasHeight = u24(dataStart + 7) + 1;
		} else if (id === 'ANMF' && size >= 16) {
			// Frame position (units of 2) and dimensions (value + 1).
			const x  = u24(dataStart)      * 2;
			const y  = u24(dataStart + 3)  * 2;
			const fw = u24(dataStart + 6)  + 1;
			const fh = u24(dataStart + 9)  + 1;
			// Duration in milliseconds (24-bit LE).
			const duration = u24(dataStart + 12);
			// Flags byte — bit 0: dispose method, bit 1: blend method.
			const flags         = u8[dataStart + 15];
			const disposeMethod = flags & 1;        // 0=keep, 1=clear to transparent
			const blendMethod   = (flags >> 1) & 1; // 0=alpha-blend, 1=replace
			// Raw frame image data (VP8 / VP8L / VP8X+ALPH chunks).
			const frameData = buffer.slice(dataStart + 16, dataStart + size);

			frames.push({ x, y, fw, fh, duration, blendMethod, disposeMethod, frameData });
		}

		// RIFF chunks are padded to an even byte boundary.
		offset = dataStart + size + (size & 1);
	}

	if (!frames.length || !canvasWidth || !canvasHeight) return null;
	return { canvasWidth, canvasHeight, frames };
}

/**
 * Wrap raw ANMF frame chunk data in a minimal RIFF/WEBP container so the
 * static single-image decoder can decode it as a standalone WebP file.
 */
function wrapFrameInWebP(frameData, fw, fh) {
	const src = new Uint8Array(frameData);
	const tag = src.length >= 4
		? String.fromCharCode(src[0], src[1], src[2], src[3]) : '';

	let payload;

	if (tag === 'VP8X' && src.length >= 18) {
		// Copy and patch the existing VP8X chunk.
		// Clear the Animation flag (bit 6 @ byte 8): some encoders copy the
		// top-level VP8X into ANMF frames, causing the static decoder to look
		// for ANIM/ANMF sub-chunks that aren't present.
		// Also fix the canvas-size fields to match the frame dimensions.
		payload = new Uint8Array(src);
		payload[8] &= ~0x40;               // clear Animation bit (bit 6)
		payload[12] = (fw - 1) & 0xFF;     // canvas width-1  (24-bit LE)
		payload[13] = ((fw - 1) >> 8) & 0xFF;
		payload[14] = ((fw - 1) >> 16) & 0xFF;
		payload[15] = (fh - 1) & 0xFF;     // canvas height-1 (24-bit LE)
		payload[16] = ((fh - 1) >> 8) & 0xFF;
		payload[17] = ((fh - 1) >> 16) & 0xFF;
	} else if (tag === 'ALPH') {
		// The encoder omitted VP8X entirely and placed the ALPH chunk first.
		// The static decoder requires VP8X before ALPH, so synthesize a
		// minimal VP8X with only the Alpha bit (bit 3 = 0x08) set and prepend it.
		const vp8x = new Uint8Array(18);
		const dvx  = new DataView(vp8x.buffer);
		vp8x[0]=86; vp8x[1]=80; vp8x[2]=56; vp8x[3]=88; // 'VP8X'
		dvx.setUint32(4, 10, true);            // VP8X payload size = 10
		vp8x[8] = 0x08;                        // Alpha flag (bit 3)
		vp8x[12] = (fw - 1) & 0xFF;            // canvas width-1
		vp8x[13] = ((fw - 1) >> 8) & 0xFF;
		vp8x[14] = ((fw - 1) >> 16) & 0xFF;
		vp8x[15] = (fh - 1) & 0xFF;            // canvas height-1
		vp8x[16] = ((fh - 1) >> 8) & 0xFF;
		vp8x[17] = ((fh - 1) >> 16) & 0xFF;
		payload = new Uint8Array(18 + src.length);
		payload.set(vp8x, 0);
		payload.set(src, 18);
	} else {
		// VP8 (no alpha) or VP8L (lossless) — wrap as-is.
		payload = src;
	}

	const out = new Uint8Array(12 + payload.length);
	const dv  = new DataView(out.buffer);
	out[0]=82; out[1]=73; out[2]=70; out[3]=70;  // 'RIFF'
	dv.setUint32(4, 4 + payload.length, true);
	out[8]=87; out[9]=69; out[10]=66; out[11]=80; // 'WEBP'
	out.set(payload, 12);
	return out.buffer;
}

// ---------------------------------------------------------------------------

async function normalizeToArrayBuffer(data) {
	if (data instanceof ArrayBuffer) return data;

	if (ArrayBuffer.isView(data)) {
		return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
	}

	if (typeof Blob !== 'undefined' && data instanceof Blob) {
		return await data.arrayBuffer();
	}

	if(data instanceof ReadableStream) {
		return  await new Response(data).arrayBuffer();
	}

	throw new Error('Unsupported decoder input type');
}

function createCompositingSurface(width, height) {
	if (typeof OffscreenCanvas !== 'undefined') {
		const canvas = new OffscreenCanvas(width, height);
		const ctx = canvas.getContext('2d', { willReadFrequently: true });
		if (!ctx) {
			throw new Error('Unable to initialize OffscreenCanvas 2D context');
		}
		return { ctx };
	}

	if (typeof document !== 'undefined') {
		const canvas = document.createElement('canvas');
		canvas.width = width;
		canvas.height = height;
		const ctx = canvas.getContext('2d', { willReadFrequently: true });
		if (!ctx) {
			throw new Error('Unable to initialize canvas 2D context');
		}
		return { ctx };
	}

	throw new Error('No canvas implementation available for GIF compositing');
}

function drawPatchWithAlpha(targetCtx, patchImage, left, top) {
	if (typeof OffscreenCanvas !== 'undefined') {
		const patchCanvas = new OffscreenCanvas(patchImage.width, patchImage.height);
		const patchCtx = patchCanvas.getContext('2d');
		if (!patchCtx) {
			throw new Error('Unable to initialize patch OffscreenCanvas 2D context');
		}
		patchCtx.putImageData(patchImage, 0, 0);
		targetCtx.drawImage(patchCanvas, left, top);
		return;
	}

	if (typeof document !== 'undefined') {
		const patchCanvas = document.createElement('canvas');
		patchCanvas.width = patchImage.width;
		patchCanvas.height = patchImage.height;
		const patchCtx = patchCanvas.getContext('2d');
		if (!patchCtx) {
			throw new Error('Unable to initialize patch canvas 2D context');
		}
		patchCtx.putImageData(patchImage, 0, 0);
		targetCtx.drawImage(patchCanvas, left, top);
		return;
	}

	throw new Error('No canvas implementation available for GIF patch drawing');
}

export class CustomDecoder{
	constructor({ data, type }) {
		console.log("Using Custom Image Decoder");
		if(type === 'image/webp') {
			return new LibWebPDecoderAdapter({data, type});
		}

		if(type === 'image/gif') {
			return new GIFDecoderAdapter({data, type});
		}

		throw new Error(`CustomDecoder only supports image/webp and image/gif, got ${type}`);
	}
}

export default class {
	static useCustomDecoder = false;
	static get ImageDecoder() {
		return (typeof window !== 'undefined' && typeof window.ImageDecoder === 'function' && !this.useCustomDecoder)? window.ImageDecoder : CustomDecoder;
	}

	static HasDecoderSupport(mime) {
		const supportedMimes = ['image/webp', 'image/gif'];
		if(supportedMimes.includes(mime)) {
			return true;
		}

		return typeof window !== 'undefined' && typeof window.ImageDecoder === 'function';
	}

	static CanDecodeURL(url) {
		 const mime = getMimeTypeFromExtension(url);
		return this.HasDecoderSupport(mime);
	}

}
